/**
 * GeoJSON Geometry shape validation.
 *
 * Returns true if the input is a structurally-valid GeoJSON Geometry
 * (Point, MultiPoint, LineString, MultiLineString, Polygon, MultiPolygon,
 * or GeometryCollection). Does NOT validate Feature or FeatureCollection
 * wrappers — those should be unwrapped to their .geometry / .features[i].geometry
 * before checking with this helper.
 *
 * Why this exists: the SLP backend has two write paths for Collection.survey_area:
 *   1. POST/PUT /api/collections/:id/survey-area — stores user-drawn GeoJSON Geometry
 *   2. POST /api/import/csp-list — at cspImport.js:56 stores `{name: <string>}`
 *      when the CSP payload includes a location_name (a cosmetic label, not GeoJSON)
 *
 * The frontend's SurveyAreaMiniMap / SurveyAreaDrawer feed survey_area directly to
 * Leaflet's L.geoJSON(), which throws "Invalid GeoJSON object" on the {name: ...}
 * shape. That throw — without a local error boundary — propagates to App.tsx's
 * top-level boundary and replaces the whole app with "Something went wrong",
 * effectively blocking access to the affected collection.
 *
 * This helper lets the frontend skip the Leaflet call cleanly and render
 * nothing instead of crashing, until the backend schema mismatch is fixed
 * in a follow-up KI.
 */

const VALID_GEOMETRY_TYPES = [
  "Point",
  "MultiPoint",
  "LineString",
  "MultiLineString",
  "Polygon",
  "MultiPolygon",
  "GeometryCollection",
] as const;

type ValidGeometryType = (typeof VALID_GEOMETRY_TYPES)[number];

export const isValidGeometry = (g: any): boolean => {
  if (!g || typeof g !== "object") return false;
  if (typeof g.type !== "string") return false;
  if (!VALID_GEOMETRY_TYPES.includes(g.type as ValidGeometryType)) return false;
  if (g.type === "GeometryCollection") {
    return Array.isArray(g.geometries);
  }
  return Array.isArray(g.coordinates);
};
